/* global __BUILD_ID__ */
import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Auto-update watcher (T39 P0 #9 — R10; auto-reload 2026-06-18).
 *
 * The bundle embeds its own __BUILD_ID__ (minted in config-overrides.js) and a
 * matching /version.json (same id) ships with each deploy. We poll /version.json
 * on load, focus, visibility-change, and every 60s; when the live id differs from
 * the loaded build, a newer deploy is out and we reload AUTOMATICALLY — no manual
 * hard-refresh:
 *   - tab hidden / not focused → reload immediately (safe, user isn't looking);
 *   - tab visible/active       → show a short "Updating…" toast, then reload.
 * Reload is cache-busting (clears CacheStorage if present). The nginx origin now
 * serves index.html with `Cache-Control: no-cache`, so the reload always revalidates
 * the shell and picks up the new content-hashed asset URLs.
 *
 * Loop guard (deploy race): we remember the id we last reloaded FOR in
 * sessionStorage. If version.json is briefly ahead of the served bundle (mid-deploy)
 * we reload at most once per target id, then wait for the next genuinely-new id —
 * never a reload storm.
 */
const LOCAL_ID = typeof __BUILD_ID__ !== "undefined" ? String(__BUILD_ID__) : "dev";
const POLL_MS = 60 * 1000;
const RELOAD_GUARD_KEY = "ov-reloaded-to-v";
const TOAST_SECONDS = 5;

function bustingReload() {
  try {
    if (window.caches && caches.keys) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .finally(() => window.location.reload());
      return;
    }
  } catch (e) {
    /* CacheStorage unavailable — fall through to a plain reload */
  }
  window.location.reload();
}

export default function UpdateBanner() {
  const [pendingId, setPendingId] = useState(null); // newer id detected while visible → toast + reload
  const [countdown, setCountdown] = useState(TOAST_SECONDS);
  const reloadedForRef = useRef(null);

  const reloadFor = useCallback((liveId) => {
    try {
      sessionStorage.setItem(RELOAD_GUARD_KEY, liveId);
    } catch (e) {
      /* best-effort */
    }
    reloadedForRef.current = liveId;
    bustingReload();
  }, []);

  useEffect(() => {
    let active = true;

    const alreadyHandled = (liveId) => {
      if (reloadedForRef.current === liveId) return true;
      try {
        return sessionStorage.getItem(RELOAD_GUARD_KEY) === liveId;
      } catch (e) {
        return false;
      }
    };

    const onStale = (liveId) => {
      if (alreadyHandled(liveId)) return;
      if (document.visibilityState !== "visible") {
        reloadFor(liveId); // user isn't looking — reload now
      } else {
        setPendingId(liveId); // user is here — toast then reload (handled below)
      }
    };

    const check = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (active && data && data.v && String(data.v) !== LOCAL_ID) onStale(String(data.v));
      } catch (e) {
        // offline / 5xx / not deployed yet — skip this tick silently
      }
    };

    check();
    const timer = setInterval(check, POLL_MS);
    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reloadFor]);

  // Visible-tab path: count down, then auto-reload.
  useEffect(() => {
    if (!pendingId) return undefined;
    setCountdown(TOAST_SECONDS);
    const tick = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    const fire = setTimeout(() => reloadFor(pendingId), TOAST_SECONDS * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(fire);
    };
  }, [pendingId, reloadFor]);

  if (!pendingId) return null;

  return (
    <div role="status" style={S.wrap}>
      <span style={S.text}>
        A new version is available — updating in {countdown}s…
      </span>
      <button type="button" style={S.refresh} onClick={() => reloadFor(pendingId)}>
        Update now
      </button>
    </div>
  );
}

const S = {
  wrap: {
    position: "fixed",
    left: "50%",
    bottom: "20px",
    transform: "translateX(-50%)",
    zIndex: 2147483000,
    display: "flex",
    alignItems: "center",
    gap: "14px",
    maxWidth: "92vw",
    background: "#11182a",
    color: "#ffffff",
    padding: "12px 16px",
    borderRadius: "12px",
    boxShadow: "0 12px 40px rgba(16,24,40,0.35)",
    fontSize: "14px",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  text: { fontWeight: 500 },
  refresh: {
    appearance: "none",
    border: "none",
    cursor: "pointer",
    background: "#1B3F8F",
    color: "#ffffff",
    fontSize: "13.5px",
    fontWeight: 600,
    padding: "7px 16px",
    borderRadius: "8px",
  },
};
